import type { Problem, Session } from "./types";
import { captureError } from "../errors/errorTracking";

/** API origin; empty in development, where Vite proxies /v1 and /media. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/** Error carrying the API's problem details. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly details: Record<string, unknown>;
  readonly fieldCodes: Record<string, string>;
  readonly requestId?: string;

  constructor(p: Problem) {
    super(p.detail || p.code);
    this.status = p.status;
    this.code = p.code;
    this.fields = p.fields ?? {};
    this.details = p.details ?? {};
    this.fieldCodes = p.field_codes ?? {};
    this.requestId = p.request_id;
  }
}

/**
 * Session handling. The refresh token is an HttpOnly cookie the API sets on
 * the staff auth endpoints, so scripts (an XSS included) never see it; the
 * access token lives only in memory. A reload or a new tab restores the
 * session with one cookie refresh.
 *
 * Every tab shares that cookie, so tabs coordinate their refreshes:
 * - a Web Lock lets one tab refresh at a time, and a tab that waited reuses
 *   the session another tab got meanwhile instead of refreshing again;
 * - a BroadcastChannel hands every new session (and every sign-out) to the
 *   other tabs.
 * Where neither exists the API still accepts a refresh token presented again
 * within a few seconds of its rotation (STAFF_REFRESH_REUSE_GRACE), so
 * concurrent refreshes never look like token theft.
 */
const AUTH_PATH = "/v1/auth/staff/";
const REFRESH_PATH = `${AUTH_PATH}refresh`;
const REFRESH_LOCK = "wp.admin.refresh";
const SESSION_CHANNEL = "wp.admin.session";
/** Where the previous dashboard kept the whole session, refresh token included. */
const LEGACY_STORAGE_KEY = "wp.admin.session";

type SessionMessage = { type: "session"; session: Session } | { type: "signout" };

/**
 * Removes the session the previous dashboard saved in localStorage and returns
 * its refresh token, which is exchanged once for the cookie.
 */
export function takeLegacyRefreshToken(): string | null {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw === null) return null;
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const token = (JSON.parse(raw) as { refresh_token?: unknown } | null)?.refresh_token;
    return typeof token === "string" && token ? token : null;
  } catch {
    return null; // storage unavailable, or not JSON
  }
}

/** Runs fn holding the cross-tab lock `name`; without Web Locks, just runs it. */
function withLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  return locks ? locks.request(name, fn) : fn();
}

function openChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(SESSION_CHANNEL);
  } catch {
    return null;
  }
}

type Listener = (s: Session | null) => void;

/** One per tab (exported for tests that play several tabs). */
export class ApiClient {
  private session: Session | null = null;
  private refreshing: Promise<Session | null> | null = null;
  private restoring: Promise<Session | null> | null = null;
  private listeners = new Set<Listener>();
  private channel = openChannel();

  constructor() {
    this.channel?.addEventListener("message", (e: MessageEvent<SessionMessage>) => {
      this.apply(e.data.type === "session" ? e.data.session : null);
    });
  }

  get current() {
    return this.session;
  }

  /** Sets this tab's session and hands it to the other tabs. */
  setSession(s: Session | null) {
    this.apply(s);
    const message: SessionMessage = s ? { type: "session", session: s } : { type: "signout" };
    this.channel?.postMessage(message);
  }

  private apply(s: Session | null) {
    this.session = s;
    this.listeners.forEach((l) => l(s));
  }

  onSessionChange(l: Listener) {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  /**
   * Restores the session when the page loads: from the cookie, or once from
   * the refresh token the previous dashboard left in localStorage (the API
   * answers with the cookie). Null when signed out.
   */
  restore(): Promise<Session | null> {
    this.restoring ??= this.refresh(takeLegacyRefreshToken());
    return this.restoring;
  }

  /** Stops listening to the other tabs. */
  close() {
    this.channel?.close();
    this.channel = null;
  }

  async request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean } = {}): Promise<T> {
    const auth = opts.auth ?? true;
    const sentWith = this.session?.access_token;
    let res = await this.send(method, path, body, auth);
    if (res.status === 401 && auth && this.session) {
      // Single-flight refresh: concurrent 401s share one refresh call. A token
      // that changed meanwhile (another tab refreshed) is simply retried.
      const refreshed = this.session.access_token !== sentWith ? this.session : await this.refresh();
      if (refreshed) res = await this.send(method, path, body, auth);
    }
    if (res.status === 204) return undefined as T;
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new ApiError(data ?? { status: res.status, code: "network_error", detail: res.statusText });
      // Server faults only: 4xx is the user's input or session, not an incident.
      if (res.status >= 500) void captureError(err, { source: "api", requestId: err.requestId });
      throw err;
    }
    return data as T;
  }

  /** GETs a file (e.g. a CSV export) with the same auth and refresh as request(). */
  async download(path: string): Promise<Blob> {
    let res = await this.send("GET", path, undefined, true);
    if (res.status === 401 && this.session && (await this.refresh())) res = await this.send("GET", path, undefined, true);
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new ApiError(data ?? { status: res.status, code: "network_error", detail: res.statusText });
    }
    return res.blob();
  }

  private send(method: string, path: string, body: unknown, auth: boolean) {
    const headers: Record<string, string> = { Accept: "application/json", "Accept-Language": "vi" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (auth && this.session) headers.Authorization = `Bearer ${this.session.access_token}`;
    // The refresh cookie is scoped to the staff auth endpoints: only they need credentials.
    const credentials: RequestCredentials = path.startsWith(AUTH_PATH) ? "include" : "same-origin";
    return fetch(API_BASE_URL + path, { method, headers, credentials, body: body === undefined ? undefined : JSON.stringify(body) });
  }

  /**
   * Exchanges the refresh cookie (or a legacy token) for a new session. A
   * refused refresh signs every tab out; a network or server fault keeps the
   * session, so the next request tries again.
   */
  private refresh(legacyToken?: string | null): Promise<Session | null> {
    if (!this.refreshing) {
      const stale = this.session?.access_token;
      this.refreshing = withLock(REFRESH_LOCK, async () => {
        // Another tab refreshed while this one waited for the lock.
        if (this.session && this.session.access_token !== stale) return this.session;
        try {
          const body = legacyToken ? { refresh_token: legacyToken } : {};
          const s = await this.request<Session>("POST", REFRESH_PATH, body, { auth: false });
          this.setSession(s);
          return s;
        } catch (e) {
          if (e instanceof ApiError && (e.status === 401 || e.status === 403)) this.setSession(null);
          return null;
        }
      }).finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }
}

export const api = new ApiClient();

export const http = {
  get: <T>(path: string) => api.request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => api.request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body: unknown) => api.request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => api.request<T>("PATCH", path, body),
  del: <T = void>(path: string, body?: unknown) => api.request<T>("DELETE", path, body),
};

/** Builds a query string, skipping empty values. */
export function qs(params: Record<string, string | number | undefined | null>) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}
